<script setup lang="ts">
import "@/assets/css/vendors/tabulator.css";
import { ref, onMounted, watch } from "vue";
import api from "@/utils/axios";
import { TabulatorFull as Tabulator, type ColumnDefinition } from "tabulator-tables";
import { createIcons, icons } from "lucide";
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const router = useRouter();
const tableRef = ref<HTMLDivElement>();
const tabulator = ref<Tabulator>();

const getColumns = (): ColumnDefinition[] => [
    {
        title: t('documents.columns.code'),
        field: "code",
        width: 120,
        headerHozAlign: "center",
        hozAlign: "center",
    },
    {
        title: t('documents.columns.date'),
        field: "created_at",
        width: 120,
        formatter(cell: any) {
            const val = cell.getValue();
            return val ? dayjs(val).format('DD/MM/YYYY') : '-';
        }
    },
    {
        title: t('common.user'),
        field: "user",
        minWidth: 150,
        formatter(cell: any) {
            const user = cell.getValue();
            return user ? `<div class="font-medium">${user.first_name} ${user.last_name}</div><div class="text-xs text-slate-500">${user.dni || ''}</div>` : '-';
        }
    },
    {
        title: t('documents.columns.description'),
        field: "description",
        minWidth: 200,
        formatter(cell: any) {
            return cell.getValue() || '-';
        }
    },
    {
        title: t('documents.columns.status'),
        field: "status",
        hozAlign: "center",
        width: 120,
        cssClass: "text-sm font-medium uppercase",
        formatter(cell: any) {
            const val = cell.getValue();
            let color = "bg-slate-200";
            let label = val;
            
            if (val === 'OPEN') { color = "bg-success text-white"; label = t('publications.status.open'); }
            if (val === 'PAUSED') { color = "bg-warning text-white"; label = t('publications.status.paused'); }
            
            return `<span class="text-xs px-2 py-1 rounded uppercase ${color}">${label}</span>`;
        }
    },
    {
        title: t('documents.columns.actions'),
        headerHozAlign: "center",
        hozAlign: "center",
        cssClass: "text-sm font-medium uppercase whitespace-nowrap",
        width: 120,
        minWidth: 120,
        headerSort: false,
        formatter() {
            return `<div class="flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium uppercase">
                <button class="btn-view inline-flex items-center justify-center border border-slate-200 rounded p-1 hover:bg-slate-100" title="${t('common.view')}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </button>
            </div>`;
        },
        cellClick: (e: any, cell: any) => {
            const target = e.target as HTMLElement;
            if (target.closest('.btn-view')) {
                    const data = cell.getData() as any;
                    router.push(`/publications/${data.id}?from=dashboard`);
            }
        }
    }
];

const initTabulator = () => {
    if (tableRef.value) {
        tabulator.value = new Tabulator(tableRef.value, {
            layout: "fitColumns",
            placeholder: t('dashboard.no_pending'),
            ajaxURL: "/publications",
            ajaxConfig: { method: "GET" },
            ajaxRequestFunc: function(url, config, params) {
                return api.get(url, { params: { size: 10, status: 'OPEN,PAUSED' } })
                    .then(res => res.data);
            },
            ajaxResponse: function(url, params, response) {
                return response.data;
            },
            columns: getColumns(),
        });

        tabulator.value.on("renderComplete", () => {
            createIcons({ icons, attrs: { "stroke-width": 1.5 }, nameAttr: "data-lucide" });
        });
    }
};

onMounted(() => {
    initTabulator();
});

watch(locale, () => {
    if (tabulator.value) {
        tabulator.value.setColumns(getColumns());
    }
});

const refresh = () => {
    if (tabulator.value) {
        tabulator.value.replaceData();
    }
};

defineExpose({ refresh });
</script>

<template>
    <div class="overflow-x-auto scrollbar-hidden">
        <div ref="tableRef"></div>
    </div>
</template>
